import { useEffect, useState } from 'react'
import { CustomerContext } from '@typesApp/Customer'
import { shouldInitAuthToken } from '@utils/customerContextUtil'
import { useSession } from './useSession'
import { useSessionInfo } from './useSessionInfo'
import { useDebouncedCallback } from './useDebouncedCallback'

export const useAuthTokenInit = () => {
  const { initSession } = useSession()

  // NOTE: auth token init has dependency on customer context from commerce.
  // Local state is used to prevent multiple calls to auth token init (not disastrous, just wasteful).
  // This behaviour happens on N1, where it sends cartId at a later time,
  // resulting in subsequent redux state change which re-triggers the effect in this hook.
  const [inProgress, setInProgress] = useState(false)

  const {
    customerContext,
    authToken,
    isTokenReady,
    isTokenError,
    isSessionInitializing,
    isLoggingOut,
    isSessionExpired,
  } = useSessionInfo()

  const debouncedHandler = useDebouncedCallback((context: CustomerContext) => {
    setInProgress(true)
    initSession(context, () => setInProgress(false))
  }, 100)

  const shouldInit = shouldInitAuthToken({
    inProgress: isSessionInitializing || inProgress,
    isLoggingOut,
    isSessionExpired,
    isTokenError,
    isTokenReady,
    authToken,
    customerContext,
  })

  // init auth token and active data
  useEffect(() => {
    if (shouldInit) {
      debouncedHandler(customerContext)
      return () => debouncedHandler.cancel()
    }
  }, [shouldInit, customerContext, debouncedHandler])
}
